import {
  AlertModal,
  FileUploader,
  Header,
  PageWrapper,
} from "../../../components"
import {
  ContractSectionCards,
  ContractSummary,
  DesktopColumns,
  MobilePager,
} from "../../../domains/Contracts/components"
import {
  doc as fsDoc,
  runTransaction,
  serverTimestamp,
} from "firebase/firestore"
import { useAnalysisUpload, useShareLink, useViewport } from "../../../hooks"
import { useMemo, useRef, useState } from "react"

import { db } from "../../../configs/firebase"
import { scrollToNextUnanswered } from "../../../helpers"
import { useAuth } from "../../../contexts/Auth/AuthContext"
import { useDocumentContext } from "../../../contexts/Document/DocumentContext"
import { useTranslation } from "react-i18next"

const Dashboard = () => {
  const { user } = useAuth()
  const { setDocumentId } = useDocumentContext()
  const { t } = useTranslation()

  const [document, setDocument] = useState(null)
  const [analysisData, setAnalysisData] = useState(null)
  const [uploadError, setUploadError] = useState("")

  const [loading, setLoading] = useState(true)
  const [showSummary, setShowSummary] = useState(false)
  const [sectionStatus, setSectionStatus] = useState({})
  const [page, setPage] = useState(0)
  const pagerRef = useRef(null)

  const { isMobile, viewportWidth, pageWidth } = useViewport(48)
  const { createShare } = useShareLink()
  const { uploadAndAnalyze } = useAnalysisUpload({ user, setDocumentId })

  const [alertOpen, setAlertOpen] = useState(false)
  const [alertMsg, setAlertMsg] = useState("")
  const [alertTitle, setAlertTitle] = useState("")
  const [activeSectionIndex, setActiveSectionIndex] = useState(0)

  // Normalize sections with IDs
  const normalizedSections = useMemo(() => {
    const docId = analysisData?.documentId ?? "doc"
    return (analysisData?.sections || []).map((s, index) => ({
      ...s,
      id: s.id ?? `${docId}:${index}`,
    }))
  }, [analysisData?.sections, analysisData?.documentId])

  // Progress
  const totalSections = normalizedSections.length
  const completedSections = Object.values(sectionStatus).filter(
    (v) => v === "understood" || v === "notUnderstood"
  ).length
  const progressPercentage =
    totalSections > 0
      ? Math.round((completedSections / totalSections) * 100)
      : 0

  // Upload + analyze
  const onFileSelected = async (file) => {
    setLoading(true)
    setUploadError("") // Clear any previous errors
    setDocument(file)
    try {
      const { result } = await uploadAndAnalyze(file)
      setAnalysisData(result)
    } catch (err) {
      console.error(err)
      setUploadError(t("dashboard.failedToPickFile"))
    } finally {
      setLoading(false)
    }
  }

  const handleClose = () => {
    setDocument(null)
    setAnalysisData(null)
    setUploadError("")
    setShowSummary(false)
    setSectionStatus({})
    setLoading(true)
  }

  // Persist section status + auto-advance
  const handleSectionStatusChange = async (sectionIndex, status) => {
    setSectionStatus((prev) => {
      const next = { ...prev }
      if (status == null) delete next[sectionIndex]
      else next[sectionIndex] = status
      return next
    })

    const section = normalizedSections[sectionIndex]
    const documentId = analysisData?.documentId
    if (!section || !documentId) return

    try {
      const ref = fsDoc(db, "documents", documentId)
      await runTransaction(db, async (tx) => {
        const snap = await tx.get(ref)
        const data = snap.data() || {}
        const understoodArr = Array.isArray(data.understoodSections)
          ? data.understoodSections
          : []
        const unclearArr = Array.isArray(data.unclearSections)
          ? data.unclearSections
          : []
        const filterOut = (arr) =>
          (arr || []).filter((s) => s && s.id !== section.id)

        let nextUnderstood = filterOut(understoodArr)
        let nextUnclear = filterOut(unclearArr)
        if (status === "understood")
          nextUnderstood = [...nextUnderstood, section]
        else if (status === "notUnderstood")
          nextUnclear = [...nextUnclear, section]

        const answeredCount = nextUnderstood.length + nextUnclear.length
        const nextProgress =
          totalSections > 0 ? answeredCount / totalSections : 0

        tx.update(ref, {
          understoodSections: nextUnderstood,
          unclearSections: nextUnclear,
          reviewProgress: nextProgress,
          _updatedAt: serverTimestamp(),
        })
      })

      if (status !== null) {
        setTimeout(() => {
          const updated = { ...sectionStatus, [sectionIndex]: status }
          scrollToNextUnanswered({
            currentIndex: sectionIndex,
            sectionStatus: updated,
            totalSections,
            setActiveSectionIndex,
          })
        }, 300)
      }
    } catch (e) {
      console.error("Failed to persist section response", e)
    }
  }

  const handleShowSummary = () => setShowSummary(true)

  // Share
  const onShare = async (durationHours) => {
    try {
      const shareUrl = await createShare({
        userId: user.uid,
        documentId: analysisData?.documentId,
        durationHours,
      })
      try {
        await navigator.clipboard.writeText(shareUrl)
      } catch {
        const textArea = document.createElement("textarea")
        textArea.value = shareUrl
        document.body.appendChild(textArea)
        textArea.select()
        document.execCommand("copy")
        document.body.removeChild(textArea)
      }
      setAlertTitle(t("common.linkCopied"))
      setAlertMsg(shareUrl)
      setAlertOpen(true)
    } catch (error) {
      console.error("Error saving share data:", error)
      setAlertTitle("Error")
      setAlertMsg("Error creating share link. Please try again.")
      setAlertOpen(true)
    }
  }

  // Mobile pager scroll handler
  const handleHorizontalScroll = (e) => {
    const x = e.currentTarget.scrollLeft
    const newPage = Math.round(x / pageWidth)
    if (newPage !== page) setPage(newPage)
  }

  // Left pane builder used for both mobile & desktop
  const renderLeftPane = () =>
    !showSummary ? (
      <ContractSectionCards
        sections={normalizedSections}
        sectionStatus={sectionStatus}
        onSectionStatusChange={handleSectionStatusChange}
        onShowSummary={handleShowSummary}
        progressPercentage={progressPercentage}
        activeSectionIndex={activeSectionIndex}
        onActiveSectionChange={setActiveSectionIndex}
      />
    ) : (
      <ContractSummary
        setShowSummary={setShowSummary}
        summary={analysisData?.summary}
        sections={analysisData?.sections}
        sectionStatus={sectionStatus}
        onShare={onShare}
      />
    )

  // Memoize the rendered left pane (a React node, not a component).
  // Avoids remounts and losing wheel/touch listeners; re-create only when these change.
  const leftPane = useMemo(
    () => renderLeftPane(),
    [
      showSummary,
      document,
      analysisData,
      sectionStatus,
      progressPercentage,
      activeSectionIndex,
    ]
  )

  return (
    <PageWrapper
      variant={isMobile ? "default" : "full"}
      background="bg-light-default"
      header={<Header />}
      className={`px-0 ${isMobile ? "overflow-hidden h-screen" : ""}`}
    >
      {!analysisData && (
        <div className="w-full flex-1 flex items-center justify-center px-6">
          <FileUploader
            onFileSelected={onFileSelected}
            loading={document && loading}
            error={uploadError}
            onClearError={() => setUploadError("")}
          />
        </div>
      )}

      {document && !loading && analysisData && (
        <div className="w-full h-full ">
          {isMobile ? (
            <MobilePager
              leftPane={leftPane}
              pagerRef={pagerRef}
              page={page}
              pageWidth={pageWidth}
              viewportWidth={viewportWidth}
              document={document}
              onCloseDocument={handleClose}
              handleHorizontalScroll={handleHorizontalScroll}
              LeftPage={renderLeftPane}
            />
          ) : (
            <DesktopColumns
              leftPane={leftPane}
              document={document}
              onCloseDocument={handleClose}
            />
          )}
        </div>
      )}

      <AlertModal
        open={alertOpen}
        title={alertTitle}
        message={alertMsg}
        onClose={() => setAlertOpen(false)}
      />
    </PageWrapper>
  )
}

export default Dashboard
